<template>
  <div class="py-4 container-fluid">
    <div v-if="cashStatusReady && !cashIsOpen" class="row mb-3">
      <div class="col-12">
        <div class="alert alert-warning text-white mb-0" role="alert">
          <strong>Atención:</strong> la caja no está abierta. Puedes ver toda la pantalla, pero debes abrir caja para guardar la venta.
        </div>
      </div>
    </div>
    <div class="row">
      <!-- Panel izquierdo: Productos -->
      <div class="col-md-8">
        <div class="card mb-3">
          <div class="card-header pb-0">
              <div class="row" style="width: 100%; ">
                <div class="col-md-6">
                  <p class="mb-0 text-uppercase">Productos</p>
                </div>
                <div class="col-md-6 text-end">
                    <input 
                      type="text" 
                      class="form-control form-control-sm" 
                      v-model="searchProduct" 
                      placeholder="Buscar producto..."
                      @input="filterProducts"
                      style="width: 100%;"
                    />
                </div>
              </div>
          </div>
          <div class="card-body">
            <div v-if="productsLoading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Cargando...</span>
              </div>
            </div>
            <div v-else class="row g-3">
              <div 
                class="col-md-3 col-sm-4 col-6" 
                v-for="product in products" 
                :key="product.id"
              >
                <div 
                  class="card product-card h-100 cursor-pointer"
                  :class="{ 'border-success': productQuantityInt(product) > 0, 'opacity-50': productQuantityInt(product) <= 0 }"
                  @click="addProductToCart(product)"
                  :title="productQuantityInt(product) <= 0 ? 'Sin stock' : 'Click para agregar'"
                >
                  <span
                    class="product-stock-circle"
                    :class="productQuantityInt(product) > 0 ? 'product-stock-circle--ok' : 'product-stock-circle--empty'"
                    :title="'Stock: ' + productQuantityInt(product)"
                  >{{ productQuantityInt(product) }}</span>
                  <div class="card-body p-2 text-center">
                    <div class="mb-2" style="height: 80px; display: flex; align-items: center; justify-content: center; overflow: hidden;">
                      <img 
                        v-if="product.image" 
                        :src="apiUrl + product.image.file_path" 
                        :alt="product.name"
                        class="img-fluid"
                        style="max-height: 80px; max-width: 100%; object-fit: contain;"
                      />
                      <i v-else class="fas fa-box fa-3x text-secondary"></i>
                    </div>
                    <h6 class="mb-1 text-sm font-weight-bold">{{ product.name }}</h6>
                    <p class="mb-1 text-xs text-secondary">Ref: {{ product.ref || 'N/A' }}</p>
                    <p class="mb-0 text-xs">
                      <span class="badge bg-success">${{ formatPrice(product.sale_price) }}</span>
                    </p>
                  </div>
                </div>
              </div>
              <div v-if="products.length === 0" class="col-12 text-center py-5">
                <p class="text-muted">No se encontraron productos</p>
              </div>
            </div>
            <div
              v-if="!productsLoading && productsTotal > productsPerPage"
              class="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-3 px-2"
            >
              <span class="text-xs text-secondary">
                Mostrando {{ productsRangeStart }}-{{ productsRangeEnd }} de {{ productsTotal }}
              </span>
              <div class="d-flex align-items-center gap-2">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary mb-0"
                  :disabled="productsLoading || productsPage <= 1"
                  @click="goToProductsPage(productsPage - 1)"
                >
                  <i class="fas fa-chevron-left"></i>
                </button>
                <span class="text-xs text-secondary">{{ productsPage }} / {{ totalProductPages }}</span>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary mb-0"
                  :disabled="productsLoading || productsPage >= totalProductPages"
                  @click="goToProductsPage(productsPage + 1)"
                >
                  <i class="fas fa-chevron-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Panel derecho: Carrito -->
      <div class="col-md-4">
        <div class="card">
          <div class="card-header pb-0">
            <div class="row">
              <div class="col-md-6 col-6">
              <div class="cash-status-header">
                <p class="mb-0 text-uppercase font-weight-bold">Carrito</p>
                <span class="cash-status-pill" :class="cashIsOpen ? 'cash-status-pill--open' : 'cash-status-pill--closed'">
                  <i class="fas fa-circle me-1"></i>
                  Caja {{ cashIsOpen ? 'abierta' : 'cerrada' }}
                </span>
              </div>
              </div>
              <div class="col-md-6 col-6">
                <div class="d-flex align-items-center gap-2 ms-auto">
                  <argon-button
                    color="warning"
                    size="sm"
                    v-if="!cashIsOpen"
                    class="cash-toggle-btn"
                    @click="openCashModal"
                  >
                    Abrir caja
                  </argon-button>
                  <argon-button
                    color="warning"
                    size="sm"
                    v-if="cashIsOpen"
                    class="cash-toggle-btn"
                    @click="openCashModal"
                  >
                    Cerrar caja
                  </argon-button>
                </div>
              </div>
            </div>
          </div>
          <div class="card-body" >
            <!-- Cliente -->
            <div class="mb-3">
              <label class="form-control-label text-sm">Cliente</label>
              <div class="d-flex gap-2">
                <select 
                  ref="clientSelect"
                  class="form-select flex-grow-1" 
                  v-model="form.cliente_id" 
                  @change="selectClient"
                  :disabled="form.status == 1"
                >
                  <option value="">Seleccione cliente</option>
                  <option v-for="client in clients" :key="client.id" :value="client.id">
                    {{ client.document }} - {{ client.name }}
                  </option>
                </select>
                <argon-button 
                  v-if="form.status != 1" 
                  color="success" 
                  size="sm" 
                  @click="openAddClientModal"
                >
                  <i class="fas fa-user-plus"></i>
                </argon-button>
              </div>
            </div>

            <!-- Lista de productos en el carrito -->
            <div class="mb-3">
              <label class="form-control-label text-sm">Productos</label>
              <div 
                class="cart-items" 
                style="max-height: 300px; overflow-y: auto;"
              >
                <div 
                  v-for="item in cartItems" 
                  :key="item.id || item.tempId"
                  class="position-relative mb-2 p-2 border rounded cart-item-bg"
                >
                  <!-- Botón eliminar en esquina superior derecha -->
                  <button 
                    v-if="(user.role !== '1' && form.status === 0) || user.role === '1'"
                    class="btn btn-sm btn-link text-danger p-0 position-absolute" 
                    @click="removeFromCart(item)"
                    title="Eliminar"
                    style="top: 5px; right: 5px; z-index: 1;"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                  
                  <!-- Contenido principal -->
                  <div>
                    <!-- Nombre del producto -->
                    <p class="mb-2 text-xs font-weight-bold">{{ item.produccion?.name || item.name }}</p>
                    
                    <!-- Controles de cantidad -->
                    <div class="mb-2">
                      <div class="quantity-control-group">
                        <button 
                          v-if="(user.role !== '1' && form.status === 0) || user.role === '1'"
                          class="quantity-btn quantity-btn-minus" 
                          @click="decreaseQuantity(item)"
                          type="button"
                          :disabled="item.quantity <= 1 || form.status == 1"
                        >
                          <i class="fas fa-minus"></i>
                        </button>
                        <input 
                          type="text" 
                          class="quantity-input" 
                          :value="item.quantity"
                          disabled
                          readonly
                        />
                        <button 
                          v-if="(user.role !== '1' && form.status === 0) || user.role === '1'"
                          class="quantity-btn quantity-btn-plus" 
                          @click="increaseQuantity(item)"
                          type="button"
                          :disabled="item.quantity >= getMaxQuantity(item) || form.status == 1"
                        >
                          <i class="fas fa-plus"></i>
                        </button>
                      </div>
                    </div>
                    
                    <!-- Dos columnas: Precio unitario y Precio total -->
                    <div class="row g-2">
                      <!-- Columna izquierda: Precio unitario -->
                      <div class="col">
                        <span class="text-xs text-secondary d-block">Precio unitario</span>
                        <span class="text-xs font-weight-bold">${{ formatPrice(item.price) }}</span>
                      </div>
                      
                      <!-- Columna derecha: Precio total -->
                      <div class="col text-end">
                        <span class="text-xs text-secondary d-block">Precio total</span>
                        <span class="text-xs font-weight-bold text-success">${{ formatPrice(item.total) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="cartItems.length === 0" class="text-center py-3 text-muted">
                  <p class="mb-0 text-xs">El carrito está vacío</p>
                </div>
              </div>
            </div>

            <!-- Total -->
            <div class="mb-3 p-3 cart-total-bg rounded">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <span class="text-sm font-weight-bold">Subtotal:</span>
                <span class="text-sm font-weight-bold">${{ formatPrice(subtotal) }}</span>
              </div>
              <div class="d-flex justify-content-between align-items-center">
                <span class="text-lg font-weight-bold">TOTAL:</span>
                <span class="text-lg font-weight-bold text-success">${{ formatPrice(subtotal) }}</span>
              </div>
            </div>

            <!-- Forma de pago -->
            <div class="mb-3">
              <label class="form-control-label text-sm">Forma de pago *</label>
              <select
                v-model="payment.payment_method"
                class="form-select"
                :disabled="form.status == 1"
              >
                <option value="">Seleccione forma de pago</option>
                <option v-for="method in paymentMethods" :key="method" :value="method">
                  {{ method }}
                </option>
              </select>
            </div>
            <div class="mb-3">
              <label class="form-control-label text-sm">Valor pagado *</label>
              <input
                type="text"
                v-model="formattedPaymentPagado"
                class="form-control"
                placeholder="Ingrese el valor pagado"
                :disabled="form.status == 1"
              >
            </div>

            <!-- Botones -->
            <div class="d-grid gap-2">
              <button 
                v-if="(user.role !== '1' && form.status == 0) || (user.role === '1' && form.status == 0)" 
                class="btn btn-sm btn-success" 
                @click="finalizeAndSave"
                :disabled="!form.cliente_id || cartItems.length === 0 || !cashIsOpen || !payment.payment_method"
              >
                <i class="fas fa-save me-1"></i> Guardar
              </button>
              <button 
                v-if="form.id != 0 && form.status == 0"
                class="btn btn-sm btn-outline-danger" 
                @click="clearCart"
              >
                <i class="fas fa-times me-1"></i> Limpiar Carrito
              </button>
            </div>
          </div>
          <div class="card-footer">
            </div>
        </div>
      </div>
    </div>

    <!-- Modal Agregar Cliente -->
    <div class="modal fade" id="ModalAddClient" tabindex="-1" aria-labelledby="addClientModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content card">
          <div class="modal-header">
            <h5 class="modal-title" id="addClientModalLabel">Agregar Cliente</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <form>
              <div class="mb-3">
                <label for="client-name" class="form-label">Nombre *</label>
                <input type="text" v-model="newClient.name" id="client-name" class="form-control" placeholder="Ingrese el nombre" required>
              </div>
              <div class="mb-3">
                <label for="client-document-type" class="form-label">Tipo de Documento *</label>
                <select id="client-document-type" class="form-control" v-model="newClient.document_type" required>
                  <option value="" disabled>Seleccione un tipo de documento</option>
                  <option value="CC">Cédula de Ciudadanía (CC)</option>
                  <option value="CE">Cédula de Extranjería (CE)</option>
                  <option value="TI">Tarjeta de Identidad (TI)</option>
                  <option value="RC">Registro Civil (RC)</option>
                  <option value="PA">Pasaporte (PA)</option>
                  <option value="PEP">Permiso Especial de Permanencia (PEP)</option>
                  <option value="NIT">Número de Identificación Tributaria (NIT)</option>
                </select>
              </div>
              <div class="mb-3">
                <label for="client-document" class="form-label">Número de Documento *</label>
                <input type="text" v-model="newClient.document" id="client-document" class="form-control" placeholder="Ingrese el número de documento" required>
              </div>
              <div class="mb-3">
                <label for="client-email" class="form-label">Email</label>
                <input type="email" v-model="newClient.email" id="client-email" class="form-control" placeholder="Ingrese el email">
              </div>
              <div class="mb-3">
                <label for="client-phone" class="form-label">Teléfono</label>
                <input type="text" v-model="newClient.phone" id="client-phone" class="form-control" placeholder="Ingrese el teléfono">
              </div>
              <div class="mb-3">
                <label for="client-address" class="form-label">Dirección</label>
                <input type="text" v-model="newClient.address" id="client-address" class="form-control" placeholder="Ingrese la dirección">
              </div>
              <div class="row mb-3">
                <div class="col-6">
                  <div class="form-check form-switch mt-4">
                    <input
                      id="client-oil-reminder-enabled"
                      v-model="newClient.oil_reminder_enabled"
                      class="form-check-input"
                      type="checkbox"
                      role="switch"
                    />
                    <label class="form-check-label" for="client-oil-reminder-enabled">
                      Recordatorio de cambio de aceite
                    </label>
                  </div>
                </div>
                <div class="col-6">
                  <label for="client-oil-reminder-days" class="form-label">Recordatorio cada (días)</label>
                  <input
                    id="client-oil-reminder-days"
                    v-model="newClient.oil_reminder_days"
                    type="number"
                    min="1"
                    max="365"
                    class="form-control"
                    :disabled="!newClient.oil_reminder_enabled"
                  />
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeAddClientModal">Cerrar</button>
            <button type="button" class="btn btn-primary" @click="saveClient">Guardar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal cierre de caja (mismo flujo que Ventas / remissions) -->
    <div class="modal fade" id="ModalBudget" tabindex="-1" aria-labelledby="posCashModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content card">
          <div class="modal-header">
            <h5 class="modal-title" id="posCashModalLabel">Registrar cierre de caja</h5>
            <button type="button" class="btn-close" aria-label="Cerrar" @click="closeCashModal"></button>
          </div>
          <div class="modal-body">
            <form>
              <div class="mb-3">
                <label class="form-label">Hora de apertura</label>
                <input type="datetime-local" v-model="modal.opened_at" class="form-control" readonly>
              </div>
              <div class="mb-3" v-if="modal.id != 0">
                <label class="form-label">Hora de cierre</label>
                <input type="datetime-local" v-model="modal.closed_at" class="form-control" readonly>
              </div>
              <div class="mb-3">
                <label class="form-label">Total abrir caja</label>
                <input
                  type="text"
                  inputmode="decimal"
                  v-model="formattedOpenTotal"
                  class="form-control"
                  placeholder="0,00"
                >
                <small
                  v-if="!cashIsOpen && suggestedOpenTotal > 0"
                  class="text-muted"
                >
                  Recomendado (último total real del cierre anterior):
                  {{ formatCurrencyInput(suggestedOpenTotal) }}
                </small>
              </div>
              <div class="mb-3" v-if="modal.id != 0">
                <label class="form-label">Total pagos en ventas</label>
                <input
                  type="text"
                  class="form-control"
                  :value="formatCurrencyInput(modal.total_ventas)"
                  readonly
                >
                <small class="text-muted">Suma de pagos registrados en ventas desde la apertura de caja</small>
              </div>
              <div class="mb-3" v-if="modal.id != 0">
                <label class="form-label">Total pagos en compras</label>
                <input
                  type="text"
                  class="form-control"
                  :value="formatCurrencyInput(modal.total_compras)"
                  readonly
                >
                <small class="text-muted">Pagos de compras registradas desde la apertura de caja</small>
              </div>
              
              <div class="mb-3" v-if="modal.id != 0">
                <label class="form-label mb-2">Recaudo por forma de pago</label>
                <div class="table-responsive">
                  <table class="table table-sm align-items-center mb-0 cash-payments-table">
                    <thead>
                      <tr>
                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Forma de pago</th>
                        <th class="text-end text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Pagos ventas</th>
                        <th class="text-end text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Compras</th>
                        <th class="text-end text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Saldo real</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in cashPaymentTotals" :key="row.payment_method">
                        <td class="text-sm">{{ formatPaymentMethod(row.payment_method) }}</td>
                        <td class="text-end text-sm">{{ formatCurrencyInput(row.sales_total) }}</td>
                        <td class="text-end text-sm text-danger">{{ formatCurrencyInput(row.purchases_total) }}</td>
                        <td class="text-end text-sm fw-bold" :class="row.net_total >= 0 ? 'text-success' : 'text-danger'">
                          {{ formatCurrencyInput(row.net_total) }}
                        </td>
                      </tr>
                      <tr v-if="cashPaymentTotals.length === 0">
                        <td colspan="4" class="text-center text-muted text-xs py-3">
                          Sin movimientos por forma de pago en este turno
                        </td>
                      </tr>
                      <tr v-if="cashPaymentTotals.length > 0" class="cash-payments-total-row bg-light">
                        <td class="text-sm fw-bold">Total</td>
                        <td class="text-end text-sm fw-bold">{{ formatCurrencyInput(cashSalesGrandTotal) }}</td>
                        <td class="text-end text-sm fw-bold text-danger">{{ formatCurrencyInput(cashPurchasesGrandTotal) }}</td>
                        <td class="text-end text-sm fw-bold" :class="cashPaymentsGrandTotal >= 0 ? 'text-success' : 'text-danger'">
                          {{ formatCurrencyInput(cashPaymentsGrandTotal) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div class="mb-3" v-if="modal.id != 0">
                <label class="form-label">Total real</label>
                <input
                  type="text"
                  inputmode="decimal"
                  v-model="formattedActualTotal"
                  class="form-control"
                  placeholder="0,00"
                >
                <small class="text-muted">
                  Sugerido (apertura + ventas − compras): {{ formatCurrencyInput(cashNetTotal) }}
                </small>
              </div>
              <div class="mb-3">
                <label class="form-label">Notas</label>
                <textarea v-model="modal.notes" class="form-control" rows="2" placeholder="Comentarios adicionales"></textarea>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeCashModal">Cerrar</button>
            <button type="button" class="btn btn-primary" @click="submitCashForm">Guardar</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from 'bootstrap';
import Swal from 'sweetalert2';
import $ from 'jquery';
import 'select2/dist/css/select2.css';
import 'select2';
import ArgonButton from '../../../components/ArgonButton.vue';
import userService from '../../../services/user.service';
import inventoryService from '../../../services/inventory.service';
import cashClosuresService from '../../../services/cashClosures.service';
import { formatCurrencyInput, parseCurrencyInput } from '../../../helpers/currencyInput';

const body = document.getElementsByTagName("body")[0];

export default {
  name: "POS",
  components: { ArgonButton },
  data() {
    return {
      user: {},
      form: {
        id: 0,
        cliente_id: '',
        name: '',
        document_type: '',
        document: '',
        address: '',
        phone: '',
        email: '',
        observation: '',
        status: 0,
        type: 'sale',
      },
      products: [],
      productStockCache: {},
      searchProduct: '',
      productsPage: 1,
      productsPerPage: 12,
      productsTotal: 0,
      productsLastPage: 1,
      productsLoading: false,
      searchProductsTimer: null,
      clients: [],
      modalAddClient: null,
      newClient: {
        name: '',
        email: '',
        phone: '',
        document: '',
        document_type: '',
        address: '',
        role: '4',
        status: 1,
        payment: '0',
        oil_reminder_enabled: false,
        oil_reminder_days: 30,
      },
      cartItems: [],
      tempIdCounter: 0,
      paymentMethods: [
        'NEQUI',
        'DAVIPLATA',
        'DATAFONO',
        'EFECTIVO',
        'CREDITO',
        'TRANSFERENCIA',
      ],
      payment: {
        payment_method: 'EFECTIVO',
        amount_pagado: 0,
      },
      apiUrl: process.env.VUE_APP_API_IMAGE_URL || process.env.VUE_APP_API_BASE_URL,
      modalBudget: null,
      modal: {
        id: 0,
        user_id: 0,
        opened_at: '',
        closed_at: '',
        open_total: 0,
        total_ventas: 0,
        total_compras: 0,
        system_total: 0,
        actual_total: 0,
        difference: 0,
        notes: '',
        status: 'open',
        created_at: '',
        updated_at: '',
      },
      cashIsOpen: false,
      cashStatusReady: false,
      cashPaymentTotals: [],
      suggestedOpenTotal: 0,
    };
  },
  computed: {
    cashPaymentsGrandTotal() {
      return this.cashPaymentTotals.reduce(
        (sum, row) => sum + parseFloat(row.net_total ?? row.total ?? 0),
        0
      );
    },
    subtotal() {
      return this.cartItems.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
    },
    totalProductPages() {
      return Math.max(1, this.productsLastPage);
    },
    productsRangeStart() {
      if (this.productsTotal === 0) return 0;
      return (this.productsPage - 1) * this.productsPerPage + 1;
    },
    productsRangeEnd() {
      return Math.min(this.productsPage * this.productsPerPage, this.productsTotal);
    },
    cashNetTotal() {
      const opening = Number(this.modal.open_total || 0);
      const sales = Number(this.modal.total_ventas || 0);
      const purchases = Number(this.modal.total_compras || 0);
      return opening + sales - purchases;
    },
    cashSalesGrandTotal() {
      return this.cashPaymentTotals.reduce(
        (sum, row) => sum + parseFloat(row.sales_total || 0),
        0
      );
    },
    cashPurchasesGrandTotal() {
      return this.cashPaymentTotals.reduce(
        (sum, row) => sum + parseFloat(row.purchases_total || 0),
        0
      );
    },
    formattedOpenTotal: {
      get() {
        return formatCurrencyInput(this.modal.open_total);
      },
      set(value) {
        this.modal.open_total = parseCurrencyInput(value);
      },
    },
    formattedActualTotal: {
      get() {
        return formatCurrencyInput(this.modal.actual_total);
      },
      set(value) {
        this.modal.actual_total = parseCurrencyInput(value);
      },
    },
    formattedPaymentPagado: {
      get() {
        return this.payment.amount_pagado.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      },
      set(value) {
        this.payment.amount_pagado = Math.floor(value.replace(/\./g, '') || 0);
      },
    },
  },
  watch: {
    subtotal(newVal) {
      if (this.form.status === 0) {
        this.payment.amount_pagado = Math.floor(newVal);
      }
    },
  },
  methods: {
    formatCurrencyInput,
    formatPaymentMethod(method) {
      const labels = {
        EFECTIVO: 'Efectivo',
        NEQUI: 'Nequi',
        DAVIPLATA: 'Daviplata',
        DATAFONO: 'Datáfono',
        TRANSFERENCIA: 'Transferencia',
        CREDITO: 'Crédito',
      };
      return labels[method] || method;
    },
    applyCashClosureExtras(cash) {
      if (!cash) {
        this.cashPaymentTotals = [];
        return;
      }
      this.modal.total_ventas = Number(cash.total_ventas) || 0;
      this.modal.total_compras = Number(cash.total_compras) || 0;
      const suggestedTotal = Number(cash.system_total) || (
        Number(this.modal.open_total || 0) + this.modal.total_ventas - this.modal.total_compras
      );
      this.modal.system_total = suggestedTotal;
      this.cashPaymentTotals = Array.isArray(cash.payment_totals) ? cash.payment_totals : [];
      if (this.modal.id !== 0) {
        this.modal.actual_total = suggestedTotal;
      }
    },
    formatPrice(price) {
      return formatCurrencyInput(price);
    },
    productQuantityInt(product) {
      const q = Number(product?.quantity);
      if (!Number.isFinite(q)) return 0;
      return Math.max(0, Math.floor(q));
    },
    goToProductsPage(page) {
      const p = Math.max(1, Math.min(page, this.totalProductPages));
      if (p === this.productsPage) return;
      this.getProducts(p);
    },
    filterProducts() {
      clearTimeout(this.searchProductsTimer);
      this.searchProductsTimer = setTimeout(() => {
        this.getProducts(1);
      }, 350);
    },
    cacheProductStock(product) {
      this.productStockCache[product.id] = this.productQuantityInt(product);
    },
    async loadProductImages(productsData) {
      return productsData.map((product) => {
        let productImage = null;
        if (product.media && Array.isArray(product.media) && product.media.length > 0) {
          productImage = product.media[0];
        }
        return { ...product, image: productImage };
      });
    },
    async getProducts(page = this.productsPage) {
      this.productsLoading = true;
      try {
        const params = {
          page,
          per_page: this.productsPerPage,
          status: 1,
        };
        const search = this.searchProduct.trim();
        if (search) {
          params.search = search;
        }

        const response = await inventoryService.getAll(params);
        const paginated = response.data;
        const productsData = paginated.data || [];

        const productsWithImages = await this.loadProductImages(productsData);

        this.products = productsWithImages;
        this.productsPage = paginated.current_page || page;
        this.productsLastPage = paginated.last_page || 1;
        this.productsTotal = paginated.total ?? productsWithImages.length;

        productsWithImages.forEach((product) => this.cacheProductStock(product));
      } catch (error) {
        console.error('Error fetching products:', error);
        this.products = [];
        this.productsTotal = 0;
        this.productsLastPage = 1;
      } finally {
        this.productsLoading = false;
      }
    },
    async getClients() {
      try {
        const response = await userService.getAll('all');
        const allUsers = response.data.data || response.data || [];
        this.clients = allUsers.filter(user => user.role === '4');
        
        // Inicializar select2 después de cargar los clientes
        this.$nextTick(() => {
          this.initClientSelect2();
        });
      } catch (error) {
        console.error('Error fetching clients:', error);
      }
    },
    initClientSelect2() {
      if (this.$refs.clientSelect) {
        const select = $(this.$refs.clientSelect);
        
        // Destruir select2 si ya está inicializado
        if (select.hasClass("select2-hidden-accessible")) {
          select.select2('destroy');
        }

        select.off('change');
        
        // Inicializar select2
        select.select2({
          placeholder: 'Seleccione cliente',
          allowClear: true,
          width: '100%',
        });
        
        select.on('change', (e) => {
          this.form.cliente_id = e.target.value;
          this.selectClient();
        });

        if (this.form.cliente_id) {
          select.val(String(this.form.cliente_id)).trigger('change');
        } else {
          this.selectDefaultClientDocumentZero();
        }
      }
    },
    /** Cliente con documento "0" (ej. consumidor final) */
    selectDefaultClientDocumentZero() {
      const defaultClient = this.clients.find(
        (c) => String(c.document ?? '').trim() === '0'
      );
      if (!defaultClient) {
        this.form.cliente_id = '';
        return;
      }
      this.form.cliente_id = String(defaultClient.id);
      if (this.$refs.clientSelect) {
        const $s = $(this.$refs.clientSelect);
        if ($s.hasClass('select2-hidden-accessible')) {
          $s.val(this.form.cliente_id).trigger('change');
        } else {
          this.selectClient();
        }
      } else {
        this.selectClient();
      }
    },
    selectClient() {
      const selectedClient = this.clients.find(c => c.id == this.form.cliente_id);
      if (selectedClient) {
        this.form.name = selectedClient.name;
        this.form.email = selectedClient.email || '';
        this.form.phone = selectedClient.phone || '';
        this.form.document = selectedClient.document || '';
        this.form.document_type = selectedClient.document_type || '';
        this.form.address = selectedClient.address || '';
      }
    },
    openAddClientModal() {
      const modalElement = document.getElementById('ModalAddClient');
      this.modalAddClient = new Modal(modalElement);
      this.modalAddClient.show();
    },
    closeAddClientModal() {
      if (this.modalAddClient) {
        this.modalAddClient.hide();
      }
      this.newClient = {
        name: '',
        email: '',
        phone: '',
        document: '',
        document_type: '',
        address: '',
        role: '4',
        status: 1,
        payment: '0',
        oil_reminder_enabled: false,
        oil_reminder_days: 30,
      };
    },
    generateRandomClientEmail() {
      const randomHash = Math.random().toString(36).slice(2, 10);
      return `cliente+${randomHash}@mistock.co`;
    },
    async saveClient() {
      try {
        if (!this.newClient.name || !this.newClient.document || !this.newClient.document_type) {
          Swal.fire('Error', 'Por favor complete todos los campos requeridos', 'error');
          return;
        }

        const generatedEmail = this.newClient.email?.trim() || this.generateRandomClientEmail();
        const generatedPhone = this.newClient.phone?.trim() || this.newClient.document?.trim() || '3000000000';

        const clientData = {
          ...this.newClient,
          oil_reminder_enabled: Boolean(this.newClient.oil_reminder_enabled),
          oil_reminder_days: this.newClient.oil_reminder_enabled
            ? (parseInt(this.newClient.oil_reminder_days, 10) || 30)
            : 30,
          email: generatedEmail,
          phone: generatedPhone,
          address: this.newClient.address || '',
          password: this.newClient.document + '-Alavision',
          password_confirmation: this.newClient.document + '-Alavision',
        };

        const response = await userService.createUser(clientData);
        
        if (response && response.status >= 200 && response.status < 300) {
          Swal.fire('Éxito', 'Cliente creado correctamente', 'success');
          this.closeAddClientModal();
          this.form.cliente_id = String(response.data.id);
          await this.getClients();
          this.selectClient();
        } else {
          throw new Error(response?.data?.message || 'Error al crear el cliente');
        }
      } catch (error) {
        console.error('Error creating client:', error);
        const errorMsg = error.response?.data?.message || error.message || 'Error al crear el cliente';
        Swal.fire('Error', errorMsg, 'error');
      }
    },
    addProductToCart(product) {
      this.cacheProductStock(product);
      if (this.productQuantityInt(product) <= 0) {
        Swal.fire('Advertencia', 'Este producto no tiene stock disponible', 'warning');
        return;
      }

      // Verificar si el producto ya está en el carrito
      const existingItem = this.cartItems.find(item => 
        (item.production_id || item.produccion?.id) == product.id
      );

      if (existingItem) {
        // Si ya existe, aumentar cantidad
        const newQuantity = existingItem.quantity + 1;
        if (newQuantity > this.productQuantityInt(product)) {
          Swal.fire('Advertencia', 'No hay suficiente stock disponible', 'warning');
          return;
        }
        existingItem.quantity = newQuantity;
        existingItem.total = Math.floor(existingItem.price * newQuantity);
      } else {
        // Si no existe, agregar nuevo item
        const newItem = {
          tempId: ++this.tempIdCounter,
          production_id: product.id,
          produccion: product,
          name: product.name,
          quantity: 1,
          price: parseFloat(product.sale_price) || 0,
          total: parseFloat(product.sale_price) || 0,
        };
        this.cartItems.push(newItem);
      }
      this.playCartAddSound();
    },
    /** Sonido corto al añadir al carrito (Web Audio API, sin archivos). */
    playCartAddSound() {
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        if (!this._cartAudioCtx) {
          this._cartAudioCtx = new AC();
        }
        const ctx = this._cartAudioCtx;
        if (ctx.state === 'suspended') {
          void ctx.resume();
        }
        const now = ctx.currentTime;
        const beep = (freq, start, duration) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(freq, start);
          gain.gain.setValueAtTime(0.0001, start);
          gain.gain.exponentialRampToValueAtTime(0.11, start + 0.015);
          gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.start(start);
          osc.stop(start + duration + 0.02);
        };
        beep(880, now, 0.07);
        beep(1174.66, now + 0.07, 0.09);
      } catch (e) {
        console.warn('playCartAddSound:', e);
      }
    },
    removeFromCart(item) {
      Swal.fire({
        title: '¿Eliminar producto?',
        text: 'Este producto será eliminado del carrito',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#d33',
      }).then((result) => {
        if (result.isConfirmed) {
          const index = this.cartItems.findIndex(cartItem => 
            (cartItem.id && cartItem.id === item.id) || 
            (cartItem.tempId && cartItem.tempId === item.tempId)
          );
          if (index > -1) {
            this.cartItems.splice(index, 1);
          }
        }
      });
    },
    getMaxQuantity(item) {
      const productId = item.production_id || item.produccion?.id;
      if (this.productStockCache[productId] !== undefined) {
        return this.productStockCache[productId];
      }
      const product = item.produccion;
      return product ? this.productQuantityInt(product) : 0;
    },
    increaseQuantity(item) {
      const maxQty = this.getMaxQuantity(item);
      if (item.quantity < maxQty) {
        item.quantity++;
        this.updateItemTotal(item);
      } else {
        Swal.fire('Advertencia', 'No hay suficiente stock disponible', 'warning');
      }
    },
    decreaseQuantity(item) {
      if (item.quantity > 1) {
        item.quantity--;
        this.updateItemTotal(item);
      }
    },
    updateItemTotal(item) {
      if (item.quantity < 1) {
        item.quantity = 1;
      }
      const maxQty = this.getMaxQuantity(item);
      if (item.quantity > maxQty) {
        item.quantity = maxQty;
        Swal.fire('Advertencia', 'No hay suficiente stock disponible', 'warning');
      }
      item.total = Math.floor(item.price * item.quantity);
    },
    async syncSoldProductsToWordPress(cartItems) {
      const productIds = [
        ...new Set(
          (cartItems || [])
            .map((item) => item.production_id || item.produccion?.id)
            .filter((id) => id != null && id !== '')
        ),
      ];

      for (const productId of productIds) {
        try {
          await this.$store.dispatch('inventory/syncWordPress', productId);
        } catch (error) {
          console.warn(`No se pudo sincronizar producto ${productId} con WordPress:`, error);
        }
      }
    },
    async generateInvoice(remissionId) {
      try {
        await this.$router.push({
          name: "PosTicket",
          params: { id: String(remissionId) },
          query: { returnTo: "/pos" },
        });
      } catch (error) {
        console.error("Error al abrir ticket:", error);
        const msg =
          error?.data?.message ||
          error?.response?.data?.message ||
          "No se pudo abrir el ticket.";
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: msg,
        });
        throw error;
      }
    },
    clearCart() {
      Swal.fire({
        title: '¿Limpiar carrito?',
        text: 'Esta acción eliminará todos los productos del carrito',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, limpiar',
        cancelButtonText: 'Cancelar'
      }).then((result) => {
        if (result.isConfirmed) {
          this.cartItems = [];
        }
      });
    },
    async getData() {
      if (this.form.id && this.form.id !== 0) {
        try {
          const response = await this.$store.dispatch('remissionsDetails/getData', this.form.id);
          if (response && response.data && response.data.data) {
            this.cartItems = response.data.data;
          }
        } catch (error) {
          console.error('Error fetching cart items:', error);
        }
      }
    },
    async finalizeAndSave() {
      this.form.status = 1;
      await this.save();
    },
    async save() {
      try {
        if (!this.form.cliente_id) {
          Swal.fire('Error', 'Por favor seleccione un cliente', 'error');
          return;
        }

        if (this.cartItems.length === 0) {
          Swal.fire('Error', 'Por favor agregue al menos un producto al carrito', 'error');
          return;
        }

        if (!this.payment.payment_method) {
          Swal.fire('Error', 'Seleccione una forma de pago', 'error');
          return;
        }

        if (this.payment.amount_pagado <= 0) {
          Swal.fire('Error', 'Ingrese el valor pagado', 'error');
          return;
        }

        const currentUser = JSON.parse(localStorage.getItem('user'));
        if (!currentUser || !currentUser.id) {
          Swal.fire('Error', 'No se pudo obtener la información del usuario', 'error');
          return;
        }

        // El backend solo descuenta inventario en update() al pasar de borrador a formalizada (status 0 → 1).
        // Si creamos/actualizamos ya con status 1 antes de los detalles, nunca se ejecuta esa lógica.
        const formalizeAfterDetails = Number(this.form.status) === 1;

        const remissionData = {
          id: this.form.id || 0,
          user_id: currentUser.id,
          cliente_id: this.form.cliente_id,
          observation: this.form.observation || '',
          type: this.form.type || 'sale',
          status: formalizeAfterDetails ? 0 : (this.form.status || 0),
        };

        const response = await this.$store.dispatch("remissions/save", remissionData);
        if (response && response.status >= 200 && response.status < 300) {
          this.form.id = response.data.id;

          // Guardar todos los items del carrito
          const savePromises = [];
          for (const item of this.cartItems) {
            const detailData = {
              id: item.id || 0,
              remission_id: this.form.id,
              production_id: item.production_id || item.produccion?.id,
              model_id: 0, // En POS no usamos modelos, solo productos del inventario
              quantity: item.quantity,
              price: item.price,
              total: item.total ?? Math.floor(item.price * item.quantity),
            };

            savePromises.push(this.$store.dispatch("remissionsDetails/save", detailData));
          }

          // Esperar a que todos los detalles se guarden
          const detailResponses = await Promise.all(savePromises);
          
          // Verificar si hubo errores
          const hasErrors = detailResponses.some(response => 
            !response || response.status < 200 || response.status >= 300
          );

          if (hasErrors) {
            throw new Error('Error al guardar algunos detalles de la venta');
          }

          const paymentResponse = await this.$store.dispatch('remissionsPayments/save', {
            id: 0,
            remission_id: this.form.id,
            payment_method: this.payment.payment_method,
            amount_pagado: this.payment.amount_pagado,
          });

          if (!paymentResponse || paymentResponse.status < 200 || paymentResponse.status >= 300) {
            throw new Error(paymentResponse?.data?.message || 'Error al registrar el pago');
          }

          if (formalizeAfterDetails) {
            const finalizeResponse = await this.$store.dispatch("remissions/save", {
              id: this.form.id,
              user_id: currentUser.id,
              cliente_id: this.form.cliente_id,
              observation: this.form.observation || '',
              type: this.form.type || 'sale',
              status: 1,
            });
            if (!finalizeResponse || finalizeResponse.status < 200 || finalizeResponse.status >= 300) {
              const apiMsg = finalizeResponse?.data?.message || finalizeResponse?.data?.error;
              throw new Error(apiMsg || 'No se pudo formalizar la venta ni actualizar el inventario.');
            }
          }

          const message = this.form.status == 1 
            ? 'La venta se guardó correctamente. Las cantidades se han restado del inventario.'
            : 'El registro ha sido guardado correctamente.';
          
          await Swal.fire({
            icon: 'success',
            title: '¡Éxito!',
            text: message,
            confirmButtonText: 'Aceptar',
            backdrop: true,
          });

          if (this.form.status == 1) {
            if (formalizeAfterDetails) {
              try {
                await this.syncSoldProductsToWordPress(this.cartItems);
              } catch (error) {
                console.warn('Error al sincronizar stock con WordPress:', error);
              }
            }

            const savedRemissionId = this.form.id;

            // Limpiar y reiniciar antes de abrir el ticket (al volver el POS queda listo)
            this.form.id = 0;
            this.form.observation = '';
            this.form.status = 0;
            this.cartItems = [];
            this.payment = {
              payment_method: 'EFECTIVO',
              amount_pagado: 0,
            };

            this.$nextTick(() => {
              this.selectDefaultClientDocumentZero();
            });

            await this.getProducts();

            try {
              await this.generateInvoice(savedRemissionId);
            } catch (error) {
              console.error('Error al abrir ticket:', error);
            }
          } else {
            // Si solo se guardó, recargar los items para obtener los IDs
            await this.getData();
          }
        } else {
          throw new Error(response.data?.message || 'Error al guardar la venta');
        }
      } catch (error) {
        console.error("Error al crear la venta:", error);
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.message || 'Error al guardar la venta',
        });
      }
    },
    async refreshCashClosureTotals() {
      const uid = this.user?.id;
      if (!uid || !this.cashIsOpen) {
        return;
      }
      try {
        const cash = await this.$store.dispatch('cashClosures/getData', uid);
        if (cash && cash.id) {
          this.applyCashClosureExtras(cash);
        }
      } catch (e) {
        console.warn('No se pudo actualizar el total de cierre:', e);
      }
    },
    async loadSuggestedOpenTotal() {
      if (this.cashIsOpen) {
        return;
      }
      try {
        const response = await cashClosuresService.getAll({ page: 'all' });
        const list = Array.isArray(response.data)
          ? response.data
          : (response.data?.data || []);
        const uid = Number(this.user?.id);
        const lastClosed = list.find(
          (closure) => closure.status === 'closed' && Number(closure.user_id) === uid
        ) || list.find((closure) => closure.status === 'closed');
        const amount = lastClosed ? parseFloat(lastClosed.actual_total) || 0 : 0;
        this.suggestedOpenTotal = amount;
        if (amount > 0) {
          this.modal.open_total = amount;
        }
      } catch (error) {
        console.warn('No se pudo cargar el total real del último cierre:', error);
        this.suggestedOpenTotal = 0;
      }
    },
    async openCashModal() {
      if (this.cashIsOpen) {
        await this.refreshCashClosureTotals();
      } else {
        await this.loadSuggestedOpenTotal();
      }
      const modalElement = document.getElementById('ModalBudget');
      this.modal.closed_at = this.formatToDateTimeLocal();
      this.modalBudget = new Modal(modalElement);
      this.modalBudget.show();
    },
    closeCashModal() {
      if (this.modalBudget) {
        this.modalBudget.hide();
      }
    },
    async submitCashForm() {
      try {
        const wasOpening = !this.cashIsOpen;
        const payload = { ...this.modal };
        if (this.cashIsOpen) {
          payload.system_total = this.cashNetTotal;
          payload.actual_total = parseFloat(this.modal.actual_total) || 0;
          delete payload.difference;
        } else {
          delete payload.difference;
          payload.system_total = 0;
          payload.actual_total = 0;
        }
        const response = await this.$store.dispatch('cashClosures/save', payload);
        if (response && response.status >= 200 && response.status < 300) {
          await Swal.fire({
            icon: 'success',
            title: '¡Éxito!',
            text: wasOpening ? 'Caja abierta correctamente.' : 'Caja cerrada correctamente.',
            confirmButtonText: 'Aceptar',
            backdrop: true,
          }).then((result) => {
            if (result.isConfirmed) {
              this.closeCashModal();
              this.fetchCash();
            }
          });
        } else {
          throw new Error(response?.data?.message || 'Error al guardar el registro');
        }
      } catch (error) {
        console.error('Error al guardar caja:', error);
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.message || 'Error al guardar el registro',
        });
      }
    },
    formatToDateTimeLocal() {
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    },
    async fetchCash() {
      const uid = this.user?.id;
      this.cashPaymentTotals = [];
      this.modal = {
        id: 0,
        user_id: uid || 0,
        opened_at: this.formatToDateTimeLocal(),
        closed_at: '',
        open_total: 0,
        total_ventas: 0,
        total_compras: 0,
        system_total: 0,
        actual_total: 0,
        difference: 0,
        notes: '',
        status: 'open',
        created_at: '',
        updated_at: '',
      };
      if (!uid) {
        this.cashIsOpen = false;
        this.cashStatusReady = true;
        return;
      }
      try {
        const cash = await this.$store.dispatch('cashClosures/getData', uid);
        if (cash && cash.id) {
          this.cashIsOpen = true;
          this.modal = {
            ...cash,
            opened_at: cash.opened_at ? cash.opened_at.slice(0, 16) : '',
            closed_at: this.formatToDateTimeLocal(),
            status: 'closed',
            open_total: parseFloat(cash.open_total) || 0,
            total_ventas: parseFloat(cash.total_ventas) || 0,
            total_compras: parseFloat(cash.total_compras) || 0,
            system_total: parseFloat(cash.system_total) || 0,
            actual_total: parseFloat(cash.system_total) || 0,
          };
          this.applyCashClosureExtras(cash);
        } else {
          this.cashIsOpen = false;
          await this.loadSuggestedOpenTotal();
        }
      } catch (e) {
        console.warn('No se pudo consultar el estado de caja:', e);
        this.cashIsOpen = false;
        await this.loadSuggestedOpenTotal();
      } finally {
        this.cashStatusReady = true;
      }
    },
  },
  async mounted() {
    try {
      this.user = JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      this.user = {};
    }
    this.modal.user_id = this.user?.id || 0;
    await this.fetchCash();
    await this.getProducts();
    await this.getClients();
    this.$store.state.isAbsolute = false;
  },
  beforeMount() {
    this.$store.state.imageLayout = "profile-overview";
    this.$store.state.showNavbar = true;
    this.$store.state.showFooter = true;
    this.$store.state.hideConfigButton = true;
    body.classList.add("profile-overview");
  },
  beforeUnmount() {
    clearTimeout(this.searchProductsTimer);
    // Destruir select2 antes de desmontar el componente
    if (this.$refs.clientSelect) {
      const select = $(this.$refs.clientSelect);
      if (select.hasClass("select2-hidden-accessible")) {
        select.select2('destroy');
      }
    }
    
    this.$store.state.isAbsolute = false;
    this.$store.state.imageLayout = "default";
    this.$store.state.showNavbar = true;
    this.$store.state.showFooter = true;
    this.$store.state.hideConfigButton = false;
    body.classList.remove("profile-overview");
  }
};
</script>

<style scoped>
.product-card {
  position: relative;
  transition: all 0.3s ease;
  cursor: pointer;
}

.product-stock-circle {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: #fff;
  line-height: 1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  pointer-events: none;
}

.product-stock-circle--ok {
  background: linear-gradient(87deg, #2dce89 0, #24a46d 100%);
}

.product-stock-circle--empty {
  background: linear-gradient(87deg, #f5365c 0, #c0341d 100%);
}

.product-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.product-card.border-success {
  border: 2px solid #28a745 !important;
}

.cursor-pointer {
  cursor: pointer;
}

.cart-item-bg {
  background-color: #f8f9fa;
}

.cart-total-bg {
  background-color: #f8f9fa;
}

.cash-status-header {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.cash-status-pill {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 0.28rem 0.7rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.cash-status-pill i {
  font-size: 0.45rem;
}

.cash-status-pill--open {
  background: rgba(45, 206, 137, 0.14);
  color: #138f5a;
  border: 1px solid rgba(45, 206, 137, 0.35);
}

.cash-status-pill--closed {
  background: rgba(245, 54, 92, 0.12);
  color: #c93755;
  border: 1px solid rgba(245, 54, 92, 0.3);
}

.cash-toggle-btn {
  min-width: 118px;
  border-radius: 10px !important;
  font-weight: 700;
}

.cart-items::-webkit-scrollbar {
  width: 6px;
}

.cart-items::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.cart-items::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.cart-items::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Estilos para select2 del cliente */
.select2-container {
  width: 100% !important;
}

.select2-container--default .select2-selection--single {
  height: 31.5px !important;
  border: 1px solid #d2d6da !important;
  border-radius: 0.375rem !important;
}

.select2-container--default .select2-selection--single .select2-selection__rendered {
  line-height: 29.5px !important;
  padding-left: 0.5rem !important;
  padding-right: 20px !important;
}

.select2-container--default .select2-selection--single .select2-selection__arrow {
  height: 29.5px !important;
  right: 8px !important;
  top: 1px !important;
}

.select2-container--default .select2-selection--single .select2-selection__arrow b {
  border-color: #6c757d transparent transparent transparent !important;
  border-width: 5px 4px 0 4px !important;
  margin-top: -2px !important;
}

/* Estilos para controles de cantidad */
.quantity-control-group {
  display: inline-flex;
  align-items: center;
  border: 1px solid #d2d6da;
  border-radius: 0.375rem;
  overflow: hidden;
  background-color: #fff;
}

.quantity-btn {
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  background-color: #f8f9fa;
  color: #495057;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 0;
}

.quantity-btn:hover:not(:disabled) {
  background-color: #e9ecef;
  color: #212529;
}

.quantity-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.quantity-btn i {
  font-size: 10px;
}

.quantity-input {
  width: 60px;
  height: 32px;
  padding: 0.25rem 0.5rem;
  border: none;
  border-left: 1px solid #d2d6da;
  border-right: 1px solid #d2d6da;
  text-align: center;
  background-color: #fff;
  font-size: 0.875rem;
  color: #495057;
}

.quantity-input:focus {
  outline: none;
  box-shadow: none;
}

.cash-payments-table {
  border-collapse: collapse;
  width: 100%;
}

.cash-payments-table th,
.cash-payments-table td {
  border: 1px solid #dee2e6;
  padding: 0.5rem 0.75rem;
  vertical-align: middle;
}

.cash-payments-table thead th {
  background-color: #f8f9fa;
}

.cash-payments-total-row td {
  border-top: 1px solid #dee2e6;
}
</style>

<style>
body.dark-version .cart-item-bg {
  background-color: #2a2a2a !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}

body.dark-version .cart-total-bg {
  background-color: #2a2a2a !important;
}

body.dark-version .quantity-control-group {
  background-color: #2a2a2a !important;
  border-color: rgba(255, 255, 255, 0.15) !important;
}

body.dark-version .quantity-btn {
  background-color: #333 !important;
  color: #fff !important;
}

body.dark-version .quantity-btn:hover:not(:disabled) {
  background-color: #444 !important;
  color: #fff !important;
}

body.dark-version .quantity-input {
  background-color: #2a2a2a !important;
  color: #fff !important;
  border-left-color: rgba(255, 255, 255, 0.15) !important;
  border-right-color: rgba(255, 255, 255, 0.15) !important;
}

body.dark-version .product-card {
  background-color: #1f1f1f !important;
}

body.dark-version .cart-items::-webkit-scrollbar-track {
  background: #1a1a1a;
}

body.dark-version .cart-items::-webkit-scrollbar-thumb {
  background: #555;
}

body.dark-version .cash-payments-table th,
body.dark-version .cash-payments-table td {
  border-color: rgba(255, 255, 255, 0.1) !important;
}

body.dark-version .cash-payments-table thead th {
  background-color: #2a2a2a !important;
}

body.dark-version .bg-light {
  background-color: #2a2a2a !important;
}
</style>
